import { motion } from 'framer-motion';
import { Calendar, MapPin, Plus } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import { typography } from '../../../styles/typography';
import { getAllSites } from '../../SiteManagement/siteApi';
import { Card } from '../../ui';
import Input from '../../ui/input';
import { stockInApi } from '../Stock_in/stockInApi';
import { closingStockApi } from './CloseingStockApi';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Helper to generate random date within a range
const getRandomDate = (startDate, endDate) => {
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();
  const randomTime = start + Math.random() * (end - start);
  return new Date(randomTime).toISOString().split('T')[0];
};

// Fallback sites data (used if API fails)
const fallbackSites = [
  { site_id: 'site_001', site_name: 'Bangalore Main Office' },
  { site_id: 'site_002', site_name: 'Mumbai Branch' },
  { site_id: 'site_003', site_name: 'Delhi Office' },
  { site_id: 'site_004', site_name: 'Chennai Warehouse' },
  { site_id: 'site_005', site_name: 'Hyderabad Center' },
];

// Dummy item names for variety
const itemNames = [
  'Rice Bags',
  'Cooking Oil',
  'Bottled Water',
  'Cleaning Supplies',
  'Packaging Boxes',
  'Wheat Flour',
  'Sugar',
  'Salt',
  'Spices',
  'Vegetables',
  'Fruits',
  'Dairy Products',
  'Meat',
  'Poultry',
  'Seafood',
  'Beverages',
  'Snacks',
  'Canned Goods',
  'Frozen Foods',
  'Paper Products',
];

// Generate dummy closing stock data with random dates
// This will be called with actual sites to ensure matching IDs
const generateDummyClosingStockData = (sites = []) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  
  // Generate dates from 2 months ago to 1 month ahead to ensure data across months
  const startDate = new Date(currentYear, currentMonth - 2, 1);
  const endDate = new Date(currentYear, currentMonth + 2, 0); // Last day of month ahead
  
  const items = [];
  
  // Use provided sites or fallback
  const availableSites = sites.length > 0 ? sites : [
    { site_id: 'site_001', site_name: 'Bangalore Main Office' },
    { site_id: 'site_002', site_name: 'Mumbai Branch' },
    { site_id: 'site_003', site_name: 'Delhi Office' },
  ];
  
  // Generate items with random dates - ensure at least some items per site per month
  const itemCount = 50; // Increased to ensure good distribution
  
  for (let i = 1; i <= itemCount; i++) {
    const randomSiteIndex = Math.floor(Math.random() * availableSites.length);
    const selectedSite = availableSites[randomSiteIndex];
    const randomItemName = itemNames[Math.floor(Math.random() * itemNames.length)];
    const randomDate = getRandomDate(startDate, endDate);
    
    items.push({
      id: i,
      name: `${randomItemName} ${i > itemNames.length ? `(Batch ${Math.floor(i / itemNames.length) + 1})` : ''}`,
      openingStockDate: randomDate,
      siteId: selectedSite.site_id || selectedSite.id,
      siteName: selectedSite.site_name || selectedSite.name,
    });
  }
  
  return items;
};

const CloseStockView = () => {
  // Log screen view
  useScreenLogging('CloseStockView', {
    component: 'CloseStockView',
    screenType: 'view-closing-stock',
  });

  const navigate = useNavigate();
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [closingStockItems, setClosingStockItems] = useState([]);
  const [isLoadingStock, setIsLoadingStock] = useState(false);
  const [stockError, setStockError] = useState('');
  const [openingDate, setOpeningDate] = useState('');
  const [summary, setSummary] = useState(null);
  const [lastKey, setLastKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // Snapshots state (shown when site is selected)
  const [snapshots, setSnapshots] = useState([]);
  const [isLoadingSnapshots, setIsLoadingSnapshots] = useState(false);
  const [snapshotsError, setSnapshotsError] = useState('');
  const [snapshotsLastKey, setSnapshotsLastKey] = useState(null);
  const [hasMoreSnapshots, setHasMoreSnapshots] = useState(false);

  // Fetch sites from API on component mount
  useEffect(() => {
    const fetchSites = async () => {
      setIsSitesLoading(true);
      try {
        const response = await getAllSites();
        // Handle different response formats
        const siteList = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
          ? response
          : [];
        
        // Sort sites by name
        const sorted = [...siteList].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        
        const finalSites = sorted.length > 0 ? sorted : fallbackSites;
        setSites(finalSites);
        
        // Don't auto-select - let user select from dropdown
      } catch (error) {
        console.error('Error fetching sites, using fallback:', error);
        // Use fallback sites on error
        setSites(fallbackSites);
        // Don't auto-select - let user select from dropdown
      } finally {
        setIsSitesLoading(false);
      }
    };

    fetchSites();
  }, []);

  // Fetch snapshots when site is selected
  useEffect(() => {
    if (!selectedSite) {
      setSnapshots([]);
      setSnapshotsError('');
      return;
    }

    const fetchSnapshots = async (reset = true) => {
      setIsLoadingSnapshots(true);
      setSnapshotsError('');

      try {
        const siteId = selectedSite.site_id || selectedSite.id;
        
        // Calculate start_date and end_date from selectedMonth
        let startDate = undefined;
        let endDate = undefined;
        if (selectedMonth) {
          const [year, month] = selectedMonth.split('-');
          const yearNum = parseInt(year, 10);
          const monthNum = parseInt(month, 10);
          
          // Create start date (first day of month)
          const start = new Date(Date.UTC(yearNum, monthNum - 1, 1, 0, 0, 0));
          // Create end date (last day of month)
          const end = new Date(Date.UTC(yearNum, monthNum, 0, 23, 59, 59));
          
          // Format as YYYY-MM-DD
          const formatDate = (date) => {
            const y = date.getUTCFullYear();
            const m = String(date.getUTCMonth() + 1).padStart(2, '0');
            const d = String(date.getUTCDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
          };
          
          startDate = formatDate(start);
          endDate = formatDate(end);
        }
        
        const params = {
          limit: 200,
          site_id: siteId,
        };

        // Add date range if month is selected
        if (startDate && endDate) {
          params.start_date = startDate;
          params.end_date = endDate;
        }

        if (!reset && snapshotsLastKey) {
          params.last_key = snapshotsLastKey;
        }

        const result = await closingStockApi.listClosingStockSnapshots(params);

        if (result.success) {
          if (reset) {
            setSnapshots(result.snapshots || []);
          } else {
            setSnapshots(prev => [...prev, ...(result.snapshots || [])]);
          }
          setSnapshotsLastKey(result.lastKey);
          setHasMoreSnapshots(result.lastKey !== null);
        } else {
          // Show user-friendly error message
          const errorMsg = result.message || 'Failed to fetch closing stock snapshots';
          setSnapshotsError(errorMsg);
          if (reset) {
            setSnapshots([]);
          }
        }
      } catch (err) {
        console.error('Error fetching snapshots:', err);
        // Check if it's a CORS or network error
        const errorMsg = err.message && (err.message.includes('Failed to fetch') || err.message.includes('CORS'))
          ? 'Unable to connect to the API. This may be a server configuration issue. Please try again later or contact support.'
          : 'Failed to load closing stock snapshots. Please try again.';
        setSnapshotsError(errorMsg);
        if (reset) {
          setSnapshots([]);
        }
      } finally {
        setIsLoadingSnapshots(false);
      }
    };

    fetchSnapshots(true);
  }, [selectedSite, selectedMonth]);

  // Fetch closing stock data when opening date changes (only if no site selected)
  useEffect(() => {
    if (selectedSite) {
      // If site is selected, don't fetch current stock
      setClosingStockItems([]);
      setSummary(null);
      return;
    }

    const fetchClosingStock = async () => {
      if (!openingDate) {
        // If no opening date, fetch current stock
        setIsLoadingStock(true);
        setStockError('');
        try {
          console.log('CloseStockView: Fetching closing stock (no opening date)');
          const result = await closingStockApi.getClosingStock({
            limit: 500,
            date: new Date().toISOString().split('T')[0],
          });
          
          console.log('CloseStockView: API result:', {
            success: result.success,
            itemsCount: result.items?.length || 0,
            summary: result.summary,
          });

          if (result.success) {
            // Store summary data from API
            setSummary(result.summary);
            setLastKey(result.lastKey);
            setHasMore(result.lastKey !== null);
            
            // Check if there are any items
            if (result.items && result.items.length > 0) {
              // Transform API response to match component structure
              // current_quantity is the closing stock according to API docs
              const transformedItems = result.items.map((item, index) => {
                // Log original API item to debug
                if (index === 0) {
                  console.log('CloseStockView: First API item:', JSON.stringify(item, null, 2));
                  console.log('CloseStockView: API item stock_item_id:', item.stock_item_id, 'type:', typeof item.stock_item_id);
                  console.log('CloseStockView: API item item_name:', item.item_name, 'type:', typeof item.item_name);
                }
                
                return {
                  id: item.stock_item_id || `item-${index}`,
                  name: item.item_name || 'Unknown Item',
                  quantity: item.opening_quantity !== undefined && item.opening_quantity !== null 
                    ? item.opening_quantity 
                    : 0, // Only use opening_quantity, don't fallback to current_quantity
                  closingQuantity: item.current_quantity || 0, // current_quantity is closing stock
                  unit: item.unit_of_measure || 'units',
                  stock_item_id: item.stock_item_id || null, // Preserve stock_item_id, use null if undefined
                  vendor_name: item.vendor_name,
                  reorder_threshold: item.reorder_threshold,
                  site_id: item.site_id,
                  site_name: item.site_name,
                  price_per_unit: item.price_per_unit,
                  is_active: item.is_active,
                  opening_value: item.opening_value,
                  opening_quantity: item.opening_quantity, // Preserve original opening_quantity
                  current_quantity: item.current_quantity, // Preserve original current_quantity
                  is_low_stock: item.reorder_threshold > 0 && item.current_quantity <= item.reorder_threshold,
                };
              });
              
              if (transformedItems.length > 0) {
                console.log('CloseStockView: First transformed item:', JSON.stringify(transformedItems[0], null, 2));
              }
              
              setClosingStockItems(transformedItems);
              setStockError(''); // Clear any previous errors
            } else {
              // No items found - this is expected if there are no stock items in the system
              setClosingStockItems([]);
              setStockError('');
              console.log('No closing stock items found. This could mean:');
              console.log('1. No stock items have been created yet');
              console.log('2. All stock items have zero quantity');
              console.log('3. Stock items exist but are filtered out');
            }
          } else {
            setStockError(result.message || 'Failed to fetch closing stock');
            setClosingStockItems([]);
            setSummary(null);
          }
        } catch (error) {
          console.error('Error fetching closing stock:', error);
          setStockError('Failed to load closing stock data');
          setClosingStockItems([]);
        } finally {
          setIsLoadingStock(false);
        }
      } else {
        // Fetch with opening date
        setIsLoadingStock(true);
        setStockError('');
        try {
          console.log('CloseStockView: Fetching closing stock with opening_date:', openingDate);
          const result = await closingStockApi.getClosingStock({
            limit: 500,
            opening_date: openingDate,
            date: openingDate,
          });
          
          console.log('CloseStockView: API result:', {
            success: result.success,
            itemsCount: result.items?.length || 0,
            summary: result.summary,
            firstItem: result.items?.[0],
          });

          if (result.success) {
            // Store summary data from API
            setSummary(result.summary);
            setLastKey(result.lastKey);
            setHasMore(result.lastKey !== null);
            
            // Check if there are any items
            if (result.items && result.items.length > 0) {
              // Transform API response - current_quantity is closing stock
              const transformedItems = result.items.map((item, index) => ({
                id: item.stock_item_id || `item-${index}`,
                name: item.item_name || 'Unknown Item',
                quantity: item.opening_quantity || 0, // opening stock
                closingQuantity: item.current_quantity || 0, // current_quantity is closing stock
                unit: item.unit_of_measure || 'units',
                stock_item_id: item.stock_item_id,
                vendor_name: item.vendor_name,
                reorder_threshold: item.reorder_threshold,
                site_id: item.site_id,
                site_name: item.site_name,
                price_per_unit: item.price_per_unit,
                is_active: item.is_active,
                opening_value: item.opening_value,
                is_low_stock: item.reorder_threshold > 0 && item.current_quantity <= item.reorder_threshold,
              }));
              setClosingStockItems(transformedItems);
              setStockError(''); // Clear any previous errors
            } else {
              // No items found - this is expected if there are no stock items in the system
              setClosingStockItems([]);
              setStockError('');
              console.log('No closing stock items found. This could mean:');
              console.log('1. No stock items have been created yet');
              console.log('2. All stock items have zero quantity');
              console.log('3. Stock items exist but are filtered out');
            }
          } else {
            setStockError(result.message || 'Failed to fetch closing stock');
            setClosingStockItems([]);
            setSummary(null);
          }
        } catch (error) {
          console.error('Error fetching closing stock:', error);
          setStockError('Failed to load closing stock data');
          setClosingStockItems([]);
        } finally {
          setIsLoadingStock(false);
        }
      }
    };

    fetchClosingStock();
  }, [openingDate, selectedSite]);

  // Group closing stock items by date (using current date or opening date)
  const dateGroups = useMemo(() => {
    if (closingStockItems.length === 0) return [];
    
    // Use opening date if provided, otherwise use current date
    const displayDate = openingDate || new Date().toISOString().split('T')[0];
    
    // Group all items under the selected date
    return [{
      date: displayDate,
      items: closingStockItems,
      count: closingStockItems.length,
    }];
  }, [closingStockItems, openingDate]);

  // Group snapshots by date
  const snapshotDateGroups = useMemo(() => {
    if (!selectedMonth || !selectedSite) return [];
    
    // Parse selected month to get year and month
    const [year, month] = selectedMonth.split('-');
    const yearNum = parseInt(year, 10);
    const monthNum = parseInt(month, 10);
    
    // Get site ID for filtering
    const siteId = selectedSite?.site_id || selectedSite?.id;
    
    // Filter snapshots by site and month/year
    const filteredSnapshots = snapshots.filter(snapshot => {
      // Check if snapshot matches the selected site
      const snapshotSiteId = snapshot.site_id || snapshot.site?.site_id || snapshot.site?.id;
      if (snapshotSiteId !== siteId) {
        return false;
      }
      
      // Check if snapshot date is within the selected month
      if (!snapshot.closing_date) {
        return false;
      }
      
      const snapshotDate = new Date(snapshot.closing_date);
      const snapshotYear = snapshotDate.getFullYear();
      const snapshotMonth = snapshotDate.getMonth() + 1; // getMonth() returns 0-11
      
      return snapshotYear === yearNum && snapshotMonth === monthNum;
    });
    
    // Group filtered snapshots by closing_date
    const grouped = filteredSnapshots.reduce((acc, snapshot) => {
      const date = snapshot.closing_date || 'Unknown';
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(snapshot);
      return acc;
    }, {});
    
    // Only show dates that have snapshots
    // Convert grouped snapshots to array and filter out dates with no snapshots
    const dateGroups = Object.entries(grouped)
      .map(([date, items]) => ({
        date,
        items,
        count: items.length,
      }))
      .filter(dateGroup => dateGroup.count > 0) // Only include dates with snapshots
      .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by date (oldest first)
    
    return dateGroups;
  }, [snapshots, selectedMonth, selectedSite]);

  // Handle pagination - load more items
  const loadMoreItems = async () => {
    if (!hasMore || isLoadingStock || !lastKey) return;
    
    setIsLoadingStock(true);
    try {
      const result = await closingStockApi.getClosingStock({
        limit: 500,
        lastKey: lastKey,
        opening_date: openingDate || undefined,
        date: openingDate || new Date().toISOString().split('T')[0],
      });

      if (result.success && result.items) {
        // Append new items to existing ones
        const transformedItems = result.items.map((item, index) => ({
          id: item.stock_item_id || `item-${closingStockItems.length + index}`,
          name: item.item_name || 'Unknown Item',
          quantity: item.opening_quantity || item.current_quantity || 0,
          closingQuantity: item.current_quantity || 0,
          unit: item.unit_of_measure || 'units',
          stock_item_id: item.stock_item_id,
          vendor_name: item.vendor_name,
          reorder_threshold: item.reorder_threshold,
          site_id: item.site_id,
          site_name: item.site_name,
          price_per_unit: item.price_per_unit,
          is_active: item.is_active,
          opening_value: item.opening_value,
          is_low_stock: item.reorder_threshold > 0 && item.current_quantity <= item.reorder_threshold,
        }));
        setClosingStockItems(prev => [...prev, ...transformedItems]);
        setLastKey(result.lastKey);
        setHasMore(result.lastKey !== null);
      }
    } catch (error) {
      console.error('Error loading more items:', error);
      setStockError('Failed to load more items');
    } finally {
      setIsLoadingStock(false);
    }
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Header with Title and Create Button */}
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className={typography.h2} style={{ color: '#000000' }}>
                Close Stock
              </h1>
              <p className="mt-2 text-gray-600">
                View and manage closing stock entries
              </p>
            </div>
            <button
              onClick={() => navigate('/onboarding-close-stock/create')}
              className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              <Plus className="w-4 h-4" />
              Create Close Stock
            </button>
          </div>

          <div className="rounded-3xl">
            {/* Site Selection - When site is selected, show snapshots; otherwise show current stock */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
              <div className="min-w-[200px]">
                <label htmlFor="closing-stock-site" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <MapPin className="w-4 h-4" />
                  Select Site
                </label>
                <select
                  id="closing-stock-site"
                  value={selectedSite ? (selectedSite.site_id || selectedSite.id) : ''}
                  onChange={(e) => {
                    const siteId = e.target.value;
                    const site = sites.find(s => (s.site_id || s.id) === siteId);
                    setSelectedSite(site || null);
                  }}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSitesLoading}
                >
                  <option value="">
                    {isSitesLoading ? 'Loading sites...' : 'Select a site'}
                  </option>
                  {/* alphabetical order */}
                  {Array.isArray(sites) && sites.map((site) => (
                    <option key={site.site_id || site.id} value={site.site_id || site.id}>
                      {site.site_name || site.name || 'Unknown Site'}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedSite 
                    ? `Showing saved snapshots for ${selectedSite.site_name || selectedSite.name}`
                    : 'Select a site to view saved snapshots'}
                </p>
              </div>
              <div className="min-w-[200px]">
                <label htmlFor="closing-stock-month" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <Calendar className="w-4 h-4" />
                  Select Month
                </label>
                <Input
                  id="closing-stock-month"
                  type="month"
                  value={selectedMonth}
                  onChange={(event) => setSelectedMonth(event.target.value)}
                  className="w-full h-12 rounded-2xl border bg-white pr-4 text-sm focus:outline-none focus:ring-2 border-gray-200 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Snapshots View (when site is selected) */}
            {selectedSite && (
              <>
                {/* Snapshots Loading State */}
                {isLoadingSnapshots && snapshotDateGroups.length === 0 && (
                  <Card.Light className="p-8 text-center">
                    <p className="text-gray-500 text-lg">Loading closing stock snapshots...</p>
                  </Card.Light>
                )}

                {/* Snapshots Error State */}
                {snapshotsError && !isLoadingSnapshots && (
                  <Card.Light className="p-8 text-center">
                    <p className="text-red-500 text-lg">{snapshotsError}</p>
                  </Card.Light>
                )}

                {/* Snapshots Date Cards */}
                <div className="space-y-4">
                  {!isLoadingSnapshots && !snapshotsError && (
                    snapshotDateGroups.length > 0 ? (
                      <>
                        {snapshotDateGroups.map((dateGroup, index) => (
                          <motion.div
                            key={dateGroup.date}
                            variants={cardVariants}
                            initial="hidden"
                            animate="visible"
                            transition={{ delay: index * 0.1 }}
                          >
                            <Card.Light 
                              className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                              onClick={async () => {
                                // Snapshots contain closing quantities, but we need to fetch opening stock
                                const siteId = selectedSite?.site_id || selectedSite?.id;
                                
                                try {
                                  setIsLoadingSnapshots(true);
                                  
                                  // Collect all items from all snapshots in this date group
                                  const allSnapshotItems = [];
                                  dateGroup.items.forEach(snapshot => {
                                    if (snapshot.items && Array.isArray(snapshot.items)) {
                                      allSnapshotItems.push(...snapshot.items);
                                    }
                                  });
                                  
                                  // Debug: Log first item to check for closing_reason in API response
                                  if (allSnapshotItems.length > 0) {
                                    console.log('CloseStockView: First item from API (before extraction):', {
                                      item: allSnapshotItems[0],
                                      hasClosingReason: 'closing_reason' in allSnapshotItems[0],
                                      closingReasonValue: allSnapshotItems[0].closing_reason,
                                      allKeys: Object.keys(allSnapshotItems[0]),
                                    });
                                  }
                                  
                                  // Fetch opening stock from stock in API (most recent stock in entry)
                                  // This should match what was used when creating the closing stock
                                  let stockInOpeningStockMap = new Map();
                                  
                                  if (siteId && dateGroup.date) {
                                    try {
                                      console.log('CloseStockView: Fetching opening stock from stock in API for expanded view:', {
                                        siteId,
                                        closingDate: dateGroup.date,
                                      });
                                      
                                      const stockInResult = await stockInApi.getStockInEntries({
                                        siteId: siteId,
                                        endDate: dateGroup.date, // Get entries up to the closing date
                                        limit: 500,
                                      });
                                      
                                      if (stockInResult.success && stockInResult.entries && stockInResult.entries.length > 0) {
                                        // Group entries by stock_item_id and get the most recent one for each
                                        const entriesByItem = {};
                                        
                                        stockInResult.entries.forEach(entry => {
                                          const entryItemId = entry.stock_item_id || entry.item_id || entry.stockItemId;
                                          if (entryItemId) {
                                            const key = String(entryItemId);
                                            if (!entriesByItem[key] || !entriesByItem[key].length) {
                                              entriesByItem[key] = [];
                                            }
                                            entriesByItem[key].push(entry);
                                          }
                                        });
                                        
                                        // Sort by date (most recent first) and get the latest entry for each item
                                        Object.entries(entriesByItem).forEach(([stockItemId, entries]) => {
                                          const sortedEntries = entries.sort((a, b) => {
                                            const dateA = new Date(a.date || a.stock_date || a.event_date || 0);
                                            const dateB = new Date(b.date || b.stock_date || b.event_date || 0);
                                            return dateB - dateA; // Most recent first
                                          });
                                          
                                          const latestEntry = sortedEntries[0];
                                          const quantity = latestEntry.quantityOrWeight || latestEntry.quantity || latestEntry.quantity_or_weight || 0;
                                          
                                          stockInOpeningStockMap.set(stockItemId, quantity);
                                          console.log('CloseStockView: Found stock in opening stock for expanded view:', {
                                            stock_item_id: stockItemId,
                                            quantity,
                                            date: latestEntry.date || latestEntry.stock_date || latestEntry.event_date,
                                          });
                                        });
                                        
                                        console.log('CloseStockView: Stock in opening stock map for expanded view:', {
                                          mapSize: stockInOpeningStockMap.size,
                                          mapEntries: Array.from(stockInOpeningStockMap.entries()),
                                        });
                                      } else {
                                        console.log('CloseStockView: No stock in entries found for expanded view');
                                      }
                                    } catch (error) {
                                      console.error('CloseStockView: Error fetching stock in opening stock for expanded view:', error);
                                    }
                                  }
                                  
                                  // Get opening stock from the most recent snapshot before this date (fallback)
                                  // Opening stock = closing stock of the previous period
                                  let openingStockMap = new Map();
                                  
                                  try {
                                    // Get all snapshots for this site to find the most recent one before current date
                                    const allSnapshotsResult = await closingStockApi.listClosingStockSnapshots({
                                      site_id: siteId,
                                      limit: 200, // Get enough snapshots to find the previous one
                                    });
                                    
                                    if (allSnapshotsResult.success && allSnapshotsResult.snapshots) {
                                      const currentDate = new Date(dateGroup.date);
                                      
                                      // Get all stock_item_ids from current snapshot that we need opening stock for
                                      const currentStockItemIds = new Set(
                                        allSnapshotItems
                                          .map(item => item.stock_item_id)
                                          .filter(id => id)
                                      );
                                      
                                      console.log('CloseStockView: Looking for opening stock for stock_item_ids:', {
                                        currentStockItemIds: Array.from(currentStockItemIds),
                                        currentItems: allSnapshotItems.map(item => ({
                                          stock_item_id: item.stock_item_id,
                                          item_name: item.item_name,
                                        })),
                                      });
                                      
                                      // Find all snapshots before the current date, sorted newest first
                                      const previousSnapshots = allSnapshotsResult.snapshots
                                        .filter(snapshot => {
                                          const snapshotDate = new Date(snapshot.closing_date);
                                          return snapshotDate < currentDate;
                                        })
                                        .sort((a, b) => {
                                          const dateA = new Date(a.closing_date);
                                          const dateB = new Date(b.closing_date);
                                          return dateB - dateA; // Newest first
                                        });
                                      
                                      // For each stock_item_id in current snapshot, find its opening stock
                                      // from the most recent previous snapshot that contains it
                                      currentStockItemIds.forEach(stockItemId => {
                                        // Find the most recent previous snapshot that has this stock_item_id
                                        for (const previousSnapshot of previousSnapshots) {
                                          if (previousSnapshot.items && Array.isArray(previousSnapshot.items)) {
                                            const matchingItem = previousSnapshot.items.find(
                                              item => item.stock_item_id === stockItemId
                                            );
                                            
                                            if (matchingItem && matchingItem.closing_quantity !== undefined && matchingItem.closing_quantity !== null) {
                                              const openingQty = matchingItem.closing_quantity;
                                              openingStockMap.set(stockItemId, openingQty);
                                              console.log('CloseStockView: Found opening stock for item:', {
                                                stock_item_id: stockItemId,
                                                item_name: matchingItem.item_name,
                                                openingQty,
                                                fromSnapshotDate: previousSnapshot.closing_date,
                                                willBeUsedAsOpening: true,
                                              });
                                              break; // Found it, move to next stock_item_id
                                            }
                                          }
                                        }
                                      });
                                      
                                      if (openingStockMap.size === 0 && previousSnapshots.length > 0) {
                                        console.log('CloseStockView: No matching stock items found in previous snapshots, using most recent snapshot:', {
                                          previousDate: previousSnapshots[0].closing_date,
                                          currentDate: dateGroup.date,
                                          itemsCount: previousSnapshots[0].items?.length || 0,
                                          previousSnapshotItems: previousSnapshots[0].items?.map(item => ({
                                            stock_item_id: item.stock_item_id,
                                            item_name: item.item_name,
                                            closing_quantity: item.closing_quantity,
                                          })),
                                        });
                                      }
                                    } else {
                                      console.log('CloseStockView: No previous snapshots found');
                                      // Try to extract opening date from snapshot notes
                                      // Notes format: "Closing stock snapshot for 2025-12-10 (Opening date: 2025-12-10)"
                                      const snapshotNotes = dateGroup.items[0]?.notes || '';
                                      const openingDateMatch = snapshotNotes.match(/Opening date:\s*(\d{4}-\d{2}-\d{2})/);
                                      const extractedOpeningDate = openingDateMatch ? openingDateMatch[1] : null;
                                      
                                      console.log('CloseStockView: Extracted opening date from notes:', {
                                        notes: snapshotNotes,
                                        extractedOpeningDate,
                                      });
                                      
                                      // Try fetching opening stock from API using the opening date from notes
                                      if (extractedOpeningDate) {
                                        const openingStockResult = await closingStockApi.getClosingStock({
                                          limit: 500,
                                          date: extractedOpeningDate,
                                          openingDate: extractedOpeningDate,
                                        });
                                        
                                        if (openingStockResult.success && openingStockResult.items) {
                                          openingStockResult.items.forEach(item => {
                                            // Try opening_quantity first, then current_quantity
                                            const openingQty = item.opening_quantity !== undefined && item.opening_quantity !== null
                                              ? item.opening_quantity
                                              : (item.current_quantity !== undefined && item.current_quantity !== null
                                                ? item.current_quantity
                                                : null);
                                            
                                            if (item.stock_item_id && openingQty !== null) {
                                              openingStockMap.set(item.stock_item_id, openingQty);
                                            }
                                          });
                                        }
                                      }
                                    }
                                  } catch (error) {
                                    console.warn('CloseStockView: Error fetching previous snapshot, will use closing as opening:', error);
                                  }
                                  
                                  console.log('CloseStockView: Opening stock map:', {
                                    size: openingStockMap.size,
                                    entries: Array.from(openingStockMap.entries()),
                                    mapKeys: Array.from(openingStockMap.keys()),
                                    mapValues: Array.from(openingStockMap.values()),
                                  });
                                  
                                  // Log current snapshot items for comparison
                                  console.log('CloseStockView: Current snapshot items:', {
                                    items: allSnapshotItems.map(item => ({
                                      stock_item_id: item.stock_item_id,
                                      item_name: item.item_name,
                                      closing_quantity: item.closing_quantity,
                                    })),
                                  });
                                  
                                  // Try to extract opening stock from snapshot notes (stored when creating)
                                  // Notes are at the snapshot level, not item level
                                  const snapshotNotes = dateGroup.items[0]?.notes || '';
                                  console.log('CloseStockView: Snapshot notes for opening stock extraction:', {
                                    notes: snapshotNotes,
                                    notesLength: snapshotNotes.length,
                                    hasNotes: !!snapshotNotes,
                                  });
                                  
                                  let storedOpeningStockMap = new Map();
                                  
                                  // Try to match OPENING_STOCK_DATA pattern in notes
                                  // Pattern: OPENING_STOCK_DATA:{"stock_item_id":value}
                                  const openingStockDataMatch = snapshotNotes.match(/OPENING_STOCK_DATA:(.+?)(?:\s*\||$)/);
                                  
                                  if (openingStockDataMatch && openingStockDataMatch[1]) {
                                    try {
                                      const openingStockDataStr = openingStockDataMatch[1].trim();
                                      console.log('CloseStockView: Extracted opening stock data string:', openingStockDataStr);
                                      
                                      const openingStockData = JSON.parse(openingStockDataStr);
                                      console.log('CloseStockView: Parsed opening stock data:', openingStockData);
                                      
                                      Object.entries(openingStockData).forEach(([stockItemId, openingQty]) => {
                                        storedOpeningStockMap.set(stockItemId, openingQty);
                                        console.log('CloseStockView: Added to stored opening stock map:', {
                                          stock_item_id: stockItemId,
                                          openingQty,
                                        });
                                      });
                                      
                                      console.log('CloseStockView: Extracted opening stock from notes:', {
                                        openingStockData,
                                        mapSize: storedOpeningStockMap.size,
                                        mapEntries: Array.from(storedOpeningStockMap.entries()),
                                      });
                                    } catch (e) {
                                      console.warn('CloseStockView: Failed to parse opening stock data from notes:', {
                                        error: e,
                                        match: openingStockDataMatch[1],
                                      });
                                    }
                                  } else {
                                    console.log('CloseStockView: No OPENING_STOCK_DATA found in notes');
                                  }
                                  
                                  // Map snapshot items to the format expected by CreateClosingStocks
                                  const mappedItems = allSnapshotItems.map(item => {
                                    // IMPORTANT: Use the exact closing_quantity from snapshot (what was saved)
                                    // This ensures the displayed value matches what was created
                                    const closing = item.closing_quantity !== undefined && item.closing_quantity !== null 
                                      ? (typeof item.closing_quantity === 'string' ? parseFloat(item.closing_quantity) : item.closing_quantity)
                                      : 0;
                                    
                                    // Extract closing reason - check multiple field names (API returns closing_reason)
                                    const closingReason = item.closing_reason || item.closingReason || item.closing_note || item.reason || item.closingNote || '';
                                    console.log('CloseStockView: Extracting closing reason from item:', {
                                      stock_item_id: item.stock_item_id,
                                      item_name: item.item_name,
                                      closing_reason: item.closing_reason,
                                      closingReason: item.closingReason,
                                      closing_note: item.closing_note,
                                      reason: item.reason,
                                      extracted: closingReason,
                                      allItemKeys: Object.keys(item),
                                    });
                                    
                                    // PRIORITIZE opening stock from stock in API (most recent stock in entry)
                                    // This should match what was used when creating the closing stock
                                    let opening = closing; // Default fallback
                                    
                                    if (item.stock_item_id && stockInOpeningStockMap.has(String(item.stock_item_id))) {
                                      // Use opening stock from stock in API (most recent stock in entry)
                                      opening = stockInOpeningStockMap.get(String(item.stock_item_id));
                                      opening = typeof opening === 'string' ? parseFloat(opening) || 0 : (opening || 0);
                                    } else if (storedOpeningStockMap.has(item.stock_item_id)) {
                                      // Fallback to stored opening stock from notes
                                      opening = storedOpeningStockMap.get(item.stock_item_id);
                                    } else if (openingStockMap.has(item.stock_item_id)) {
                                      // Fallback to opening stock from previous snapshot
                                      opening = openingStockMap.get(item.stock_item_id);
                                    } else if (item.opening_quantity !== undefined && item.opening_quantity !== null) {
                                      // Fallback to opening_quantity if available in item
                                      opening = typeof item.opening_quantity === 'string' ? parseFloat(item.opening_quantity) : item.opening_quantity;
                                    }
                                    
                                    // Ensure opening and closing are numbers
                                    opening = typeof opening === 'string' ? parseFloat(opening) || 0 : (opening || 0);
                                    
                                    // Calculate consumption
                                    const consumption = Math.max(0, opening - closing);
                                    
                                    console.log('CloseStockView: Mapping item (ensuring exact values):', {
                                      stock_item_id: item.stock_item_id,
                                      item_name: item.item_name,
                                      closing_quantity_from_snapshot: item.closing_quantity,
                                      closing_parsed: closing,
                                      opening,
                                      consumption,
                                      hasStockInOpening: item.stock_item_id ? stockInOpeningStockMap.has(String(item.stock_item_id)) : false,
                                      stockInOpeningValue: item.stock_item_id ? stockInOpeningStockMap.get(String(item.stock_item_id)) : undefined,
                                      hasStoredOpening: storedOpeningStockMap.has(item.stock_item_id),
                                      storedOpeningValue: storedOpeningStockMap.get(item.stock_item_id),
                                      hasOpeningInMap: openingStockMap.has(item.stock_item_id),
                                      openingFromMap: openingStockMap.get(item.stock_item_id),
                                      source: (item.stock_item_id && stockInOpeningStockMap.has(String(item.stock_item_id))) ? 'stock_in_api' : (storedOpeningStockMap.has(item.stock_item_id) ? 'stored_notes' : (openingStockMap.has(item.stock_item_id) ? 'previous_snapshot' : 'fallback')),
                                    });
                                    
                                    return {
                                      id: item.stock_item_id || `item-${Date.now()}`,
                                      stock_item_id: item.stock_item_id || '',
                                      stockItemId: item.stock_item_id || '',
                                      name: item.item_name || '',
                                      item_name: item.item_name || '',
                                      stockName: item.item_name || '',
                                      quantity: opening,
                                      closingQuantity: closing, // Use exact closing_quantity from snapshot
                                      opening_quantity: opening,
                                      current_quantity: closing, // Use closing_quantity from snapshot (not current_quantity)
                                      closing_quantity: closing, // Preserve exact closing_quantity from snapshot
                                      unit: item.unit_of_measure || '',
                                      openingStock: String(opening),
                                      closingStock: String(closing), // Use exact closing_quantity from snapshot
                                      consumption: String(consumption.toFixed(2)),
                                      closing_reason: item.closing_reason || '', // Preserve original API field (snake_case)
                                      closingReason: closingReason, // Use extracted closing reason (camelCase for component state)
                                      vendor_name: item.vendor_name || '',
                                      price_per_unit: item.price_per_unit || 0,
                                    };
                                  });
                                  
                                  console.log('CloseStockView: Mapped snapshot items with opening stock:', {
                                    date: dateGroup.date,
                                    itemsCount: mappedItems.length,
                                    firstItem: mappedItems[0],
                                    openingStockMapSize: openingStockMap.size,
                                  });
                                  
                                  // Navigate with the mapped items - start in view mode (read-only)
                                  navigate('/onboarding-close-stock/create', {
                                    state: {
                                      date: dateGroup.date,
                                      closing_date: dateGroup.date,
                                      openingStockDate: dateGroup.date,
                                      siteId: siteId,
                                      siteName: selectedSite?.site_name || selectedSite?.name,
                                      items: mappedItems,
                                      summary: dateGroup.items[0]?.summary || null,
                                      editMode: true, // This enables view/edit toggle functionality
                                      viewMode: true, // Start in view mode (read-only) - fields will be disabled
                                    },
                                  });
                                } catch (error) {
                                  console.error('CloseStockView: Error fetching opening stock:', error);
                                  // Navigate anyway with snapshot items (opening will be same as closing)
                                  const allSnapshotItems = [];
                                  dateGroup.items.forEach(snapshot => {
                                    if (snapshot.items && Array.isArray(snapshot.items)) {
                                      allSnapshotItems.push(...snapshot.items);
                                    }
                                  });
                                  
                                  const mappedItems = allSnapshotItems.map(item => {
                                    // IMPORTANT: Use exact closing_quantity from snapshot (what was saved)
                                    const closing = item.closing_quantity !== undefined && item.closing_quantity !== null
                                      ? (typeof item.closing_quantity === 'string' ? parseFloat(item.closing_quantity) : item.closing_quantity)
                                      : 0;
                                    
                                    // Extract closing reason - check multiple field names (API returns closing_reason)
                                    const closingReason = item.closing_reason || item.closingReason || item.closing_note || item.reason || item.closingNote || '';
                                    
                                    console.log('CloseStockView: Fallback mapping item (error case):', {
                                      stock_item_id: item.stock_item_id,
                                      item_name: item.item_name,
                                      closing_quantity_from_snapshot: item.closing_quantity,
                                      closing_parsed: closing,
                                      closing_reason: item.closing_reason,
                                      closingReason: item.closingReason,
                                      extracted: closingReason,
                                      allItemKeys: Object.keys(item),
                                    });
                                    
                                    return {
                                      id: item.stock_item_id || `item-${Date.now()}`,
                                      stock_item_id: item.stock_item_id || '',
                                      stockItemId: item.stock_item_id || '',
                                      name: item.item_name || '',
                                      item_name: item.item_name || '',
                                      stockName: item.item_name || '',
                                      quantity: closing,
                                      closingQuantity: closing,
                                      opening_quantity: closing,
                                      current_quantity: closing, // Use closing_quantity from snapshot
                                      closing_quantity: closing, // Preserve exact closing_quantity from snapshot
                                      unit: item.unit_of_measure || '',
                                      openingStock: String(closing),
                                      closingStock: String(closing), // Use exact closing_quantity from snapshot
                                      consumption: '0',
                                      closingReason: closingReason, // Use extracted closing reason
                                      vendor_name: item.vendor_name || '',
                                      price_per_unit: item.price_per_unit || 0,
                                    };
                                  });
                                  
                                  navigate('/onboarding-close-stock/create', {
                                    state: {
                                      date: dateGroup.date,
                                      closing_date: dateGroup.date,
                                      openingStockDate: dateGroup.date,
                                      siteId: selectedSite?.site_id || selectedSite?.id,
                                      siteName: selectedSite?.site_name || selectedSite?.name,
                                      items: mappedItems,
                                      summary: dateGroup.items[0]?.summary || null,
                                      editMode: true, // This enables view/edit toggle functionality
                                      viewMode: true, // Start in view mode (read-only) - fields will be disabled
                                    },
                                  });
                                } finally {
                                  setIsLoadingSnapshots(false);
                                }
                              }}
                            >
                              <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-lg">
                                  {dateGroup.count}
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-xl font-semibold text-gray-800">
                                    {new Date(dateGroup.date).toLocaleDateString('en-US', {
                                      weekday: 'long',
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                    })}
                                  </h3>
                                  <p className="text-sm text-gray-600 mt-1">
                                    {dateGroup.count} {dateGroup.count === 1 ? 'snapshot' : 'snapshots'}
                                  </p>
                                  {dateGroup.items[0] && (
                                    <div className="mt-2 flex flex-wrap gap-3 text-xs">
                                      {dateGroup.items[0].total_items !== undefined && (
                                        <span className="text-gray-600">
                                          Total Items: {dateGroup.items[0].total_items}
                                        </span>
                                      )}
                                      {dateGroup.items[0].total_quantity !== undefined && (
                                        <span className="text-gray-600">
                                          Total Quantity: {dateGroup.items[0].total_quantity.toFixed(2)}
                                        </span>
                                      )}
                                      {dateGroup.items[0].low_stock_items > 0 && (
                                        <span className="text-orange-600 font-semibold">
                                          {dateGroup.items[0].low_stock_items} low stock
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Card.Light>
                          </motion.div>
                        ))}
                      </>
                    ) : (
                      <Card.Light className="p-8 text-center">
                        <p className="text-gray-500 text-lg font-semibold">
                          No snapshots found for the selected site and month
                        </p>
                        <p className="text-sm text-gray-600 mt-2">
                          Create a closing stock snapshot to see it here
                        </p>
                      </Card.Light>
                    )
                  )}

                  {/* Snapshots Pagination */}
                  {hasMoreSnapshots && !isLoadingSnapshots && (
                    <div className="flex justify-center mt-6">
                      <button
                        onClick={async () => {
                          if (!hasMoreSnapshots || isLoadingSnapshots || !snapshotsLastKey) return;
                          
                          setIsLoadingSnapshots(true);
                          try {
                            const siteId = selectedSite.site_id || selectedSite.id;
                            
                            // Calculate start_date and end_date from selectedMonth
                            let startDate = undefined;
                            let endDate = undefined;
                            if (selectedMonth) {
                              const [year, month] = selectedMonth.split('-');
                              const yearNum = parseInt(year, 10);
                              const monthNum = parseInt(month, 10);
                              
                              // Create start date (first day of month)
                              const start = new Date(Date.UTC(yearNum, monthNum - 1, 1, 0, 0, 0));
                              // Create end date (last day of month)
                              const end = new Date(Date.UTC(yearNum, monthNum, 0, 23, 59, 59));
                              
                              // Format as YYYY-MM-DD
                              const formatDate = (date) => {
                                const y = date.getUTCFullYear();
                                const m = String(date.getUTCMonth() + 1).padStart(2, '0');
                                const d = String(date.getUTCDate()).padStart(2, '0');
                                return `${y}-${m}-${d}`;
                              };
                              
                              startDate = formatDate(start);
                              endDate = formatDate(end);
                            }
                            
                            const params = {
                              limit: 200,
                              site_id: siteId,
                              last_key: snapshotsLastKey,
                            };
                            
                            // Add date range if month is selected
                            if (startDate && endDate) {
                              params.start_date = startDate;
                              params.end_date = endDate;
                            }
                            
                            const result = await closingStockApi.listClosingStockSnapshots(params);

                            if (result.success) {
                              setSnapshots(prev => [...prev, ...(result.snapshots || [])]);
                              setSnapshotsLastKey(result.lastKey);
                              setHasMoreSnapshots(result.lastKey !== null);
                            }
                          } catch (err) {
                            console.error('Error loading more snapshots:', err);
                            setSnapshotsError('Failed to load more snapshots');
                          } finally {
                            setIsLoadingSnapshots(false);
                          }
                        }}
                        className="px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors shadow-sm"
                      >
                        Load More Snapshots
                      </button>
                    </div>
                  )}

                  {/* Loading More Snapshots Indicator */}
                  {isLoadingSnapshots && hasMoreSnapshots && (
                    <Card.Light className="p-4 text-center">
                      <p className="text-gray-500">Loading more snapshots...</p>
                    </Card.Light>
                  )}
                </div>
              </>
            )}

            {/* Current Stock View (when no site is selected) */}
            {!selectedSite && (
              <>
                {/* Loading State */}
                {isLoadingStock && (
                  <Card.Light className="p-8 text-center">
                    <p className="text-gray-500 text-lg">Loading closing stock data...</p>
                  </Card.Light>
                )}

                {/* Error State */}
                {stockError && !isLoadingStock && (
                  <Card.Light className="p-8 text-center">
                    <p className="text-red-500 text-lg">{stockError}</p>
                  </Card.Light>
                )}

                {/* Date Cards with Stock Items */}
                <div className="space-y-4">
                  {!isLoadingStock && !stockError && (
                    dateGroups.length > 0 ? (
                  <>
                    {dateGroups.map((dateGroup, index) => (
                      <motion.div
                        key={dateGroup.date}
                        variants={cardVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: index * 0.1 }}
                      >
                        <Card.Light 
                          className="p-6 hover:shadow-lg transition-shadow cursor-pointer"
                          onClick={async () => {
                            const siteId = selectedSite?.site_id || selectedSite?.id;
                            const closingDate = dateGroup.date;
                            
                            // Fetch opening stock from stock in API (most recent stock in entry)
                            // This should match what was used when creating the closing stock
                            let stockInOpeningStockMap = new Map();
                            
                            if (siteId && closingDate) {
                              try {
                                console.log('CloseStockView: Fetching opening stock from stock in API:', {
                                  siteId,
                                  closingDate,
                                });
                                
                                const stockInResult = await stockInApi.getStockInEntries({
                                  siteId: siteId,
                                  endDate: closingDate, // Get entries up to the closing date
                                  limit: 500,
                                });
                                
                                if (stockInResult.success && stockInResult.entries && stockInResult.entries.length > 0) {
                                  // Group entries by stock_item_id and get the most recent one for each
                                  const entriesByItem = {};
                                  
                                  stockInResult.entries.forEach(entry => {
                                    const entryItemId = entry.stock_item_id || entry.item_id || entry.stockItemId;
                                    if (entryItemId) {
                                      const key = String(entryItemId);
                                      if (!entriesByItem[key] || !entriesByItem[key].length) {
                                        entriesByItem[key] = [];
                                      }
                                      entriesByItem[key].push(entry);
                                    }
                                  });
                                  
                                  // Sort by date (most recent first) and get the latest entry for each item
                                  Object.entries(entriesByItem).forEach(([stockItemId, entries]) => {
                                    const sortedEntries = entries.sort((a, b) => {
                                      const dateA = new Date(a.date || a.stock_date || a.event_date || 0);
                                      const dateB = new Date(b.date || b.stock_date || b.event_date || 0);
                                      return dateB - dateA; // Most recent first
                                    });
                                    
                                    const latestEntry = sortedEntries[0];
                                    const quantity = latestEntry.quantityOrWeight || latestEntry.quantity || latestEntry.quantity_or_weight || 0;
                                    
                                    stockInOpeningStockMap.set(stockItemId, quantity);
                                    console.log('CloseStockView: Found stock in opening stock for item:', {
                                      stock_item_id: stockItemId,
                                      quantity,
                                      date: latestEntry.date || latestEntry.stock_date || latestEntry.event_date,
                                    });
                                  });
                                  
                                  console.log('CloseStockView: Stock in opening stock map:', {
                                    mapSize: stockInOpeningStockMap.size,
                                    mapEntries: Array.from(stockInOpeningStockMap.entries()),
                                  });
                                } else {
                                  console.log('CloseStockView: No stock in entries found');
                                }
                              } catch (error) {
                                console.error('CloseStockView: Error fetching stock in opening stock:', error);
                              }
                            }
                            
                            // Navigate to create page with items pre-filled (same as StockOutView)
                            // dateGroup.items can be either:
                            // 1. Direct stock items (when no site selected, from getClosingStock API)
                            // 2. Snapshots with nested items (when site selected, from listClosingStockSnapshots)
                            // We need to extract the actual stock items first
                            let actualItems = [];
                            
                            // Check if items are snapshots (have nested items array) or direct items
                            if (dateGroup.items.length > 0 && dateGroup.items[0].items && Array.isArray(dateGroup.items[0].items)) {
                              // Items are snapshots - extract nested items and normalize closing_reason field
                              dateGroup.items.forEach(snapshot => {
                                if (snapshot.items && Array.isArray(snapshot.items)) {
                                  // Map items and ensure closing_reason is available as closingReason
                                  const normalizedItems = snapshot.items.map(item => ({
                                    ...item,
                                    // Preserve original closing_reason from API (snake_case)
                                    closing_reason: item.closing_reason || '',
                                    // Ensure closingReason is set from closing_reason if it exists (camelCase for component state)
                                    closingReason: item.closing_reason || item.closingReason || item.closing_note || item.reason || item.closingNote || '',
                                  }));
                                  actualItems.push(...normalizedItems);
                                }
                              });
                              
                              // Log first extracted item to check for closing_reason
                              if (actualItems.length > 0) {
                                console.log('CloseStockView: First extracted item from snapshot:', JSON.stringify(actualItems[0], null, 2));
                                console.log('CloseStockView: First extracted item keys:', Object.keys(actualItems[0]));
                                console.log('CloseStockView: First extracted item closing_reason:', actualItems[0].closing_reason);
                                console.log('CloseStockView: First extracted item closingReason:', actualItems[0].closingReason);
                              }
                            } else {
                              // Items are direct stock items - also normalize closing_reason
                              actualItems = dateGroup.items.map(item => ({
                                ...item,
                                // Preserve original closing_reason from API (snake_case)
                                closing_reason: item.closing_reason || '',
                                // Ensure closingReason is set from closing_reason if it exists (camelCase for component state)
                                closingReason: item.closing_reason || item.closingReason || item.closing_note || item.reason || item.closingNote || '',
                              }));
                            }
                            
                            console.log('CloseStockView: Processing items for navigation:', {
                              dateGroupItemsCount: dateGroup.items.length,
                              actualItemsCount: actualItems.length,
                              firstActualItem: actualItems[0],
                              isSnapshots: dateGroup.items.length > 0 && dateGroup.items[0].items && Array.isArray(dateGroup.items[0].items),
                            });
                            
                            const itemsToPass = actualItems.map(item => {
                              console.log('CloseStockView: Original item before mapping:', {
                                id: item.id,
                                stock_item_id: item.stock_item_id,
                                name: item.name,
                                item_name: item.item_name,
                                stockName: item.stockName,
                                allKeys: Object.keys(item),
                                fullItem: item,
                              });
                              
                              // Extract stock_item_id first - check if value exists and is valid
                              // Handle case where key exists but value is undefined/null/empty
                              let extractedStockItemId = null;
                              if (item.stock_item_id && item.stock_item_id !== '' && item.stock_item_id !== 'undefined' && item.stock_item_id !== null) {
                                extractedStockItemId = String(item.stock_item_id);
                              } else if (item.id && item.id !== '' && item.id !== 'undefined' && item.id !== null && 
                                        !String(item.id).startsWith('item-') && !String(item.id).startsWith('snapshot-')) {
                                extractedStockItemId = String(item.id);
                              }
                              
                              // PRIORITIZE opening stock from stock in API (most recent stock in entry)
                              // This should match what was used when creating the closing stock
                              let opening = 0;
                              
                              if (extractedStockItemId && stockInOpeningStockMap.has(extractedStockItemId)) {
                                // Use opening stock from stock in API (most recent stock in entry)
                                opening = stockInOpeningStockMap.get(extractedStockItemId);
                                opening = typeof opening === 'string' ? parseFloat(opening) || 0 : (opening || 0);
                                console.log('CloseStockView: Using opening stock from stock in API:', {
                                  stock_item_id: extractedStockItemId,
                                  opening,
                                });
                              } else {
                                // Fallback to item fields
                                opening = item.quantity || item.opening_quantity || item.openingStock || 0;
                                opening = typeof opening === 'string' ? parseFloat(opening) || 0 : (opening || 0);
                                console.log('CloseStockView: Using fallback opening stock from item fields:', {
                                  stock_item_id: extractedStockItemId,
                                  opening,
                                });
                              }
                              
                              // IMPORTANT: Use closing_quantity from snapshot (what was saved), not current_quantity
                              // Snapshots from listClosingStockSnapshots have closing_quantity field
                              const closing = item.closing_quantity !== undefined && item.closing_quantity !== null
                                ? (typeof item.closing_quantity === 'string' ? parseFloat(item.closing_quantity) : item.closing_quantity)
                                : (item.closingQuantity !== undefined && item.closingQuantity !== null
                                  ? (typeof item.closingQuantity === 'string' ? parseFloat(item.closingQuantity) : item.closingQuantity)
                                  : (item.current_quantity !== undefined && item.current_quantity !== null
                                    ? (typeof item.current_quantity === 'string' ? parseFloat(item.current_quantity) : item.current_quantity)
                                    : 0));
                              const consumption = Math.max(0, opening - closing);
                              
                              console.log('CloseStockView: Extracting values for display:', {
                                stock_item_id: extractedStockItemId,
                                item_name: item.item_name,
                                closing_quantity_from_snapshot: item.closing_quantity,
                                closingQuantity: item.closingQuantity,
                                current_quantity: item.current_quantity,
                                final_closing: closing,
                                final_opening: opening,
                                hasStoredOpening: extractedStockItemId ? storedOpeningStockMap.has(extractedStockItemId) : false,
                                storedOpeningValue: extractedStockItemId ? storedOpeningStockMap.get(extractedStockItemId) : undefined,
                              });
                              
                              // Extract item name - check multiple possible field names
                              let itemName = '';
                              if (item.item_name && item.item_name !== '' && item.item_name !== 'undefined') {
                                itemName = item.item_name;
                              } else if (item.name && item.name !== '' && item.name !== 'undefined') {
                                itemName = item.name;
                              } else if (item.stockName && item.stockName !== '' && item.stockName !== 'undefined') {
                                itemName = item.stockName;
                              }
                              
                              const mappedItem = {
                                id: extractedStockItemId || item.id || `item-${Date.now()}`,
                                stock_item_id: extractedStockItemId || '', // Preserve stock_item_id
                                stockItemId: extractedStockItemId || '', // Also include stockItemId for fallback
                                name: itemName,
                                item_name: itemName, // Also include item_name for fallback
                                stockName: itemName, // This is what CreateClosingStocks expects
                                quantity: opening,
                                closingQuantity: closing, // Use closing_quantity from snapshot
                                opening_quantity: item.opening_quantity !== undefined && item.opening_quantity !== null ? item.opening_quantity : opening,
                                current_quantity: closing, // Use closing_quantity from snapshot (not current_quantity)
                                closing_quantity: closing, // Preserve exact closing_quantity from snapshot
                                unit: item.unit || item.unit_of_measure || '',
                                openingStock: String(opening),
                                closingStock: String(closing), // Use exact closing_quantity from snapshot
                                consumption: String(consumption),
                                closing_reason: item.closing_reason || '', // Preserve original API field (snake_case)
                                closingReason: item.closing_reason || item.closingReason || item.closingNote || item.reason || item.closingNote || '', // camelCase for component state
                              };
                              
                              console.log('CloseStockView: Mapping item for navigation:', {
                                original: item,
                                mapped: mappedItem,
                                stockItemId: extractedStockItemId,
                                itemName: itemName,
                              });
                              
                              return mappedItem;
                            });
                            
                            console.log('CloseStockView: Navigating with items:', itemsToPass);
                            console.log('CloseStockView: Items count:', itemsToPass.length);
                            
                            navigate('/onboarding-close-stock/create', {
                              state: {
                                date: dateGroup.date,
                                closing_date: dateGroup.date,
                                openingStockDate: openingDate || dateGroup.date, // Pass opening date or use closing date as fallback
                                siteId: selectedSite?.site_id || selectedSite?.id,
                                siteName: selectedSite?.site_name || selectedSite?.name,
                                items: itemsToPass,
                                summary: summary,
                                editMode: true, // This enables view/edit toggle functionality
                                viewMode: true, // Start in view mode (read-only) - fields will be disabled
                              },
                            });
                          }}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-lg">
                              {dateGroup.count}
                            </div>
                            <div className="flex-1">
                              <h3 className="text-xl font-semibold text-gray-800">
                                {new Date(dateGroup.date).toLocaleDateString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                })}
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                {dateGroup.count} {dateGroup.count === 1 ? 'item' : 'items'}
                              </p>
                              {summary && (
                                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                                  {summary.total_quantity !== undefined && (
                                    <span className="text-gray-600">
                                      Total: {summary.total_quantity.toFixed(2)} {dateGroup.items[0]?.unit || 'units'}
                                    </span>
                                  )}
                                  {summary.low_stock_items > 0 && (
                                    <span className="text-orange-600 font-semibold">
                                      {summary.low_stock_items} low stock
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </Card.Light>
                      </motion.div>
                    ))}
                  </>
                ) : null
              )}

              {/* Pagination - Load More Button */}
              {hasMore && !isLoadingStock && (
                <div className="flex justify-center mt-6">
                  <button
                    onClick={loadMoreItems}
                    className="px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors shadow-sm"
                  >
                    Load More Items
                  </button>
                </div>
              )}

              {/* Loading More Indicator */}
              {isLoadingStock && hasMore && (
                <Card.Light className="p-4 text-center">
                  <p className="text-gray-500">Loading more items...</p>
                </Card.Light>
              )}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default CloseStockView;
